<template>
    <div class="form-field">
        <div class="form-field__input">
            <label
                v-if="label"
                :for="id"
                class="form-field__label"
                :class="{ 'is-active': isLabelActive }"
            >
                {{ label }}
            </label>

            <div class="input-field-wrapper">
                <input
                    :id="id"
                    ref="inputRef"
                    :type="type"
                    :value="displayValue"
                    :required="required"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    :class="[
                        'input-field',
                        { 'input-field_error': !!errorMessage },
                        { 'has-label': label },
                        { 'input-field_disabled': disabled },
                    ]"
                    @input="onInput"
                    @focus="isLabelActive = true"
                    @blur="onBlur"
                />

                <div
                    v-if="rawValue && !disabled"
                    class="input-field__clear"
                    @click.stop="clearInput"
                >
                    <img v-svg-inline src="@/assets/icons/ui/field-clear.svg" />
                </div>
            </div>
        </div>

        <span v-if="errorMessage" class="form-field__error">
            {{ errorMessage }}
        </span>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, defineProps, defineEmits, watch } from 'vue';

    const props = defineProps({
        type: {
            type: String,
            default: 'text',
        },
        required: {
            type: Boolean,
            default: false,
        },
        label: String,
        placeholder: String,
        modelValue: [String, Number],
        id: String,
        errorMessage: String,
        isCurrency: {
            type: Boolean,
            default: false,
        },
        formatNumbers: {
            type: Boolean,
            default: false,
        },
        onlyNumbers: {
            type: Boolean,
            default: false,
        },
        replacePattern: String,
        disabled: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void,
    }>();

    const isLabelActive = ref(!!props.modelValue);
    const rawValue = ref(props.modelValue ?? '');
    const inputRef = ref<HTMLInputElement | null>(null);

    const displayValue = computed(() => {
        let display = rawValue.value || '';

        if (props.formatNumbers && display) {
            const number = parseInt(String(display).replace(/\D/g, ''), 10);
            if (!isNaN(number)) {
                display = new Intl.NumberFormat('ru-RU').format(number);
            }
        }

        if (props.isCurrency && display) {
            display = `${display} ₽`;
        }

        return display;
    });

    watch(
        () => props.modelValue,
        (newVal) => {
            rawValue.value = newVal || '';
            isLabelActive.value = !!rawValue.value;
        },
        { immediate: true },
    );

    function onInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        let newValue = inputElement.value;

        const replacePattern = props.onlyNumbers || props.isCurrency || props.formatNumbers ? '\\D' : props.replacePattern;

        if (replacePattern) {
            newValue = newValue.replace(new RegExp(replacePattern, 'g'), '');
        }

        rawValue.value = newValue;
        emit('update:modelValue', rawValue.value);

        if (inputRef.value) {
            inputRef.value.value = String(displayValue.value);
        }
    }

    function onBlur(): void {
        if ((props.isCurrency || props.formatNumbers) && rawValue.value && inputRef.value) {
            inputRef.value.value = String(displayValue.value);
        }

        isLabelActive.value = !!rawValue.value;
    }

    function clearInput(): void {
        rawValue.value = '';

        emit('update:modelValue', '');
    }
</script>

<style lang="scss" scoped>
    @use './UIInput.scss' as *;
</style>
